import React, { useState, useEffect, useRef } from 'react';
import { EnhancedNavLink, EnhancedButtonLink } from './components/EnhancedNavLink';
import PageSEO from './components/PageSEO';
import { openCookiePreferences } from './components/CookieConsentBanner';
import MULTI_VOICE_DEMO_CONFIG from './multiVoiceDemoConfig';
import VoxSmythScrollIntro from './VoxSmythScrollIntro';
import UpgradeNotice from './components/UpgradeNotice';
import HomeContinueListeningHero from './components/HomeContinueListeningHero';
import VoiceOfTheWeek from './components/VoiceOfTheWeek';

// Enhanced Landing Page Component with "Literary Tech with Whisper of Magic" theme
const EnhancedLanding = ({ 
  authLoading, 
  user, 
  onLogout, // Changed from logout to onLogout to match AppRouter
  currentVoice, 
  loading, 
  isPlaying, 
  switchVoice, 
  togglePlayback, 
  demoText,
  scrollToTop,
  // Individual voice demo props
  individualVoicePlaying,
  individualVoiceLoading,
  individualVoiceTimepoint,
  lastSelectedVoice
}) => {
  // ALL HOOKS MUST BE AT THE TOP LEVEL - BEFORE ANY CONDITIONAL RETURNS
  const [particleElements, setParticleElements] = useState([]);
  const [showScrollIntro, setShowScrollIntro] = useState(() => {
    // Check if user has seen intro before on component initialization
    const hasSeenIntro = localStorage.getItem('voxsmyth-intro-seen');
    // Skip intro for bots/crawlers (prevents CLS in PageSpeed)
    const isBot = /Lighthouse|GoogleBot|PageSpeed|bot|crawl/i.test(navigator.userAgent);
    return !hasSeenIntro && !isBot;
  });
  const [introLoading, setIntroLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroGlyphRef = useRef(null);
  const [isGlyphHovered, setIsGlyphHovered] = useState(false);

  // Memoize voice name calculation to avoid hooks in conditional rendering
  const individualVoiceName = React.useMemo(() => {
    if (!individualVoicePlaying) return '';
    
    // Look for voice name in voiceAssignments
    const voiceAssignment = Object.entries(MULTI_VOICE_DEMO_CONFIG?.voiceAssignments || {})
      .find(([character, voice]) => voice.id === individualVoicePlaying);
    if (voiceAssignment) {
      return voiceAssignment[1].name;
    }
    // Fallback to hardcoded mapping for known voice IDs
    const voiceNames = {
      '4YYIPFl9wE5c4L2eu2Gb': 'Burt Reynolds™',
      'AXdMgz6evoL7OPd7eU12': 'Elizabeth',
      'AeRdCCKzvd23BpJoofzx': 'Nathaniel C',
      'rSZFtT0J8GtnLqoDoFAp': 'Nova Westbrook'
    };
    return voiceNames[individualVoicePlaying] || `Voice ID: ${individualVoicePlaying?.substring(0, 8)}...`;
  }, [individualVoicePlaying]);

  // Initialize floating particles for hero background - OPTIMIZED to prevent scroll jitter
  useEffect(() => {
    const particles = Array.from({ length: 25 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      speed: Math.random() * 0.5 + 0.1,
      opacity: Math.random() * 0.3 + 0.1,
      element: null // Will store DOM element reference
    }));
    
    // Create particle elements and store references
    const particleContainer = document.getElementById('particle-container');
    if (particleContainer) {
      particles.forEach(particle => {
        const element = document.createElement('div');
        element.style.position = 'absolute';
        element.style.width = `${particle.size}px`;
        element.style.height = `${particle.size}px`;
        element.style.backgroundColor = 'rgba(215, 179, 119, 0.6)';
        element.style.borderRadius = '50%';
        element.style.left = `${particle.x}%`;
        element.style.top = `${particle.y}%`;
        element.style.opacity = particle.opacity;
        element.style.pointerEvents = 'none';
        element.style.transition = 'none'; // Disable CSS transitions for smooth animation
        particleContainer.appendChild(element);
        particle.element = element;
      });
    }
    
    setParticleElements(particles);

    // FIXED: Animate particles using direct DOM manipulation instead of state updates
    const animateParticles = () => {
      particles.forEach(particle => {
        if (particle.element) {
          // Update particle position
          particle.y = particle.y > 100 ? -5 : particle.y + particle.speed;
          particle.opacity = Math.sin(Date.now() * 0.001 + particle.id) * 0.15 + 0.15;
          
          // Update DOM directly - NO STATE UPDATE = NO RE-RENDER
          particle.element.style.top = `${particle.y}%`;
          particle.element.style.opacity = particle.opacity;
        }
      });
    };

    const interval = setInterval(animateParticles, 50);
    
    // Cleanup function
    return () => {
      clearInterval(interval);
      // Remove particle elements from DOM
      particles.forEach(particle => {
        if (particle.element && particle.element.parentNode) {
          particle.element.parentNode.removeChild(particle.element);
        }
      });
    };
  }, []);

  // Handle body overflow based on intro visibility
  useEffect(() => {
    if (showScrollIntro) {
      document.body.style.overflow = 'hidden'; // Lock scroll during intro
    } else {
      document.body.style.overflow = 'auto'; // Allow scroll on main page
    }
    setIntroLoading(false);
  }, [showScrollIntro]);

  // Glyph heartbeat animation - MOVED UP TO BE WITH OTHER HOOKS
  useEffect(() => {
    const pulse = () => {
      if (heroGlyphRef.current) {
        heroGlyphRef.current.style.transform = 'scale(1.05) rotate(5deg)';
        setTimeout(() => {
          if (heroGlyphRef.current) {
            heroGlyphRef.current.style.transform = 'scale(1) rotate(0deg)';
          }
        }, 200);
      }
    };

    const interval = setInterval(pulse, 4000);
    return () => clearInterval(interval);
  }, []);

  // Handle intro completion
  const handleIntroComplete = () => {
    setShowScrollIntro(false);
    localStorage.setItem('voxsmyth-intro-seen', 'true'); // Mark intro as seen
    document.body.style.overflow = 'auto';
    // Scroll to top of main landing page after intro
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Voice samples for demo (using the requested voices) - MOVED UP BEFORE CONDITIONAL RETURNS
  const DEMO_VOICES = {
    'burt_reynolds': {
      id: '4YYIPFl9wE5c4L2eu2Gb',
      name: 'Burt Reynolds™',
      description: 'Iconic, charismatic legend',
      category: 'Classic Narrator'
    },
    'elizabeth': {
      id: 'AXdMgz6evoL7OPd7eU12',
      name: 'Elizabeth',
      description: 'Elegant, sophisticated British female voice',
      category: 'Character Voices'
    },
    'nathaniel_c': {
      id: 'AeRdCCKzvd23BpJoofzx',
      name: 'Nathaniel C',
      description: 'Distinguished, refined male voice',
      category: 'Character Voices'
    },
    'nova_westbrook': {
      id: 'rSZFtT0J8GtnLqoDoFAp',
      name: 'Nova Westbrook',
      description: 'Modern, dynamic female narrator',
      category: 'Full Cast Magic'
    }
  };

  // CONDITIONAL RENDERING AFTER ALL HOOKS
  // If intro is loading, show a loading state
  if (introLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#1A202C',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          color: '#D7B377',
          fontSize: '1.5rem',
          fontFamily: '"DM Serif Display", serif'
        }}>
          VoxSmyth
        </div>
      </div>
    );
  }

  // Show scroll intro for first-time visitors
  if (showScrollIntro) {
    return <VoxSmythScrollIntro onIntroComplete={handleIntroComplete} />;
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{
      background: `linear-gradient(135deg, #1A202C 0%, #2C3A4C 50%, #2C3E50 100%)`,
      position: 'relative'
    }}>
      <PageSEO
        title="Distinct Characters Deserve Distinctive Voices"
        description="AI audiobook narration that transforms any book into a multi-voice audiobook. Distinct narrator voices for each character. 65+ voices, 121 classics. Try free."
        image="https://voxsmyth.com/og/home.png"
        path="/"
      />
      <UpgradeNotice />
      {/* Constellation Background - OPTIMIZED for performance */}
      <div 
        id="particle-container" 
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{
          /* Container for dynamically created particle elements - no re-renders */
        }}
      >
        {/* Particles are now created and animated via direct DOM manipulation in useEffect */}
        {/* This prevents constant React re-renders that caused scroll jitter */}
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex justify-between items-center p-6" style={{
        background: 'rgba(26, 32, 44, 0.8)',
        backdropFilter: 'blur(10px)'
      }}>
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold" style={{
            fontFamily: 'DM Serif Display, Georgia, serif',
            color: '#D7B377',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            VoxSmyth
          </h1>
          <img 
            src="/voxsmyth-claudecolors.png" 
            alt="VoxSmyth AI audiobook narration platform" 
            width="160"
            height="40"
            style={{ 
              height: '40px', 
              width: 'auto'
            }}
          />
        </div>
        {/* Desktop Navigation */}
        <div className="hidden lg:flex space-x-6 items-center">
          <EnhancedNavLink 
            to="/"
            className="text-white hover:text-yellow-400 transition-all duration-300"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              color: '#FFFFFF' // Ensure white color
            }}
          >
            Home
          </EnhancedNavLink>
          <EnhancedNavLink 
            to="/library"
            className="text-white hover:text-yellow-400 transition-all duration-300"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              color: '#FFFFFF' // Ensure white color
            }}
          >
            Library
          </EnhancedNavLink>
          <EnhancedNavLink 
            to="/emporium"
            className="text-white hover:text-yellow-400 transition-all duration-300"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              color: '#FFFFFF'
            }}
          >
            Bookstore
          </EnhancedNavLink>
          <EnhancedNavLink 
            to="/voices"
            className="text-white hover:text-yellow-400 transition-all duration-300"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              color: '#FFFFFF' // Ensure white color
            }}
          >
            Vocal Atelier
          </EnhancedNavLink>
          <EnhancedNavLink 
            to="/pricing"
            className="text-white hover:text-yellow-400 transition-all duration-300"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              color: '#FFFFFF' // Ensure white color
            }}
          >
            Pricing
          </EnhancedNavLink>
          {/* Creator Studio - Only show for creator tier users */}
          {user && (user.tier === 'creator' || user.tier === 'super_admin') && (
            <EnhancedNavLink 
              to="/creator"
              className="text-white hover:text-yellow-400 transition-all duration-300"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#FFFFFF' // Ensure white color
              }}
            >
              Creator Studio
            </EnhancedNavLink>
          )}
          
          {/* Voice Foundry - Only show for voice actors and creators */}
          {user && (user.tier === 'voice_actor' || user.tier === 'creator' || user.tier === 'super_admin') && (
            <EnhancedNavLink 
              to="/voice-actor-portal"
              className="text-white hover:text-yellow-400 transition-all duration-300"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                color: '#FFFFFF' // Ensure white color
              }}
            >
              Voice Foundry
            </EnhancedNavLink>
          )}
        </div>
        
        {/* Mobile Navigation Menu */}
        <div className="lg:hidden">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white hover:text-yellow-400 transition-colors p-2"
            style={{ background: 'none', border: 'none' }}
            aria-label="Open navigation menu"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
              />
            </svg>
          </button>
        </div>

        {/* User Authentication Section */}
        <div className="hidden lg:flex items-center space-x-4">
          {authLoading ? (
            <div className="text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
              Loading...
            </div>
          ) : user ? (
            <>
              <EnhancedNavLink 
                to="/account"
                className="text-white hover:text-yellow-400 transition-all duration-300"
                style={{ 
                  fontFamily: 'Inter, sans-serif', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#FFFFFF' // Ensure white color
                }}
              >
                Account
              </EnhancedNavLink>
              <button 
                onClick={onLogout}
                className="px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
                  color: '#1A202C',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: '600',
                  border: 'none',
                  boxShadow: '0 4px 16px rgba(215, 179, 119, 0.3)'
                }}
              >
                Sign Out
              </button>
            </>
          ) : (
            <div className="flex gap-3">
              <EnhancedButtonLink 
                to="auth"
                className="px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                style={{
                  background: 'transparent',
                  color: '#D7B377',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: '600',
                  border: '1px solid #D7B377',
                  boxShadow: '0 2px 8px rgba(215, 179, 119, 0.2)'
                }}
              >
                Sign In
              </EnhancedButtonLink>
              <EnhancedButtonLink 
                to="auth?mode=signup"
                className="px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
                  color: '#1A202C',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: '600',
                  border: 'none',
                  boxShadow: '0 4px 16px rgba(215, 179, 119, 0.3)'
                }}
              >
                Create Account
              </EnhancedButtonLink>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute top-0 right-0 w-80 h-full bg-gray-900 shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Navigation</h2>
                <button 
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white hover:text-yellow-400 p-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <nav className="space-y-4">
                <EnhancedNavLink 
                  to="/"
                  className="block text-white hover:text-yellow-400 transition-colors py-2 border-b border-gray-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </EnhancedNavLink>
                <EnhancedNavLink 
                  to="/library"
                  className="block text-white hover:text-yellow-400 transition-colors py-2 border-b border-gray-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Library
                </EnhancedNavLink>
                <EnhancedNavLink 
                  to="/emporium"
                  className="block text-white hover:text-yellow-400 transition-colors py-2 border-b border-gray-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Bookstore
                </EnhancedNavLink>
                <EnhancedNavLink 
                  to="/voices"
                  className="block text-white hover:text-yellow-400 transition-colors py-2 border-b border-gray-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Vocal Atelier
                </EnhancedNavLink>
                <EnhancedNavLink 
                  to="/pricing"
                  className="block text-white hover:text-yellow-400 transition-colors py-2 border-b border-gray-700"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </EnhancedNavLink>
                {/* Creator Studio - Only show for creator tier users */}
                {user && (user.tier === 'creator' || user.tier === 'super_admin') && (
                  <EnhancedNavLink 
                    to="/creator"
                    className="block text-white hover:text-yellow-400 transition-colors py-2 border-b border-gray-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Creator Studio
                  </EnhancedNavLink>
                )}
                
                {/* Voice Foundry - Only show for voice actors and creators */}
                {user && (user.tier === 'voice_actor' || user.tier === 'creator' || user.tier === 'super_admin') && (
                  <EnhancedNavLink 
                    to="/voice-actor-portal"
                    className="block text-white hover:text-yellow-400 transition-colors py-2 border-b border-gray-700"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Voice Foundry
                  </EnhancedNavLink>
                )}
                
                {/* User Authentication for Mobile */}
                <div className="pt-4 border-t border-gray-700">
                  {authLoading ? (
                    <div className="text-white">Loading...</div>
                  ) : user ? (
                    <div className="space-y-3">
                      <EnhancedNavLink 
                        to="/account"
                        className="block text-white hover:text-yellow-400 transition-colors py-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Account
                      </EnhancedNavLink>
                      <button 
                        onClick={() => {
                          onLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="w-full px-4 py-3 rounded-lg text-left"
                        style={{
                          background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
                          color: '#1A202C',
                          fontWeight: '600',
                          border: 'none'
                        }}
                      >
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <EnhancedNavLink 
                        to="/auth"
                        className="block w-full px-4 py-3 rounded-lg text-center"
                        style={{
                          background: 'transparent',
                          color: '#D7B377',
                          fontWeight: '600',
                          border: '1px solid #D7B377'
                        }}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Sign In
                      </EnhancedNavLink>
                      <EnhancedNavLink 
                        to="/auth?mode=signup"
                        className="block w-full px-4 py-3 rounded-lg text-center"
                        style={{
                          background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
                          color: '#1A202C',
                          fontWeight: '600'
                        }}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Create Account
                      </EnhancedNavLink>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - "The Enchanted Introduction" */}
      <main className="relative z-10 container mx-auto px-4 md:px-6 pt-12 md:pt-20 pb-16 text-center">
        {/* Continue Listening hero — surfaces the active book from
            PlayerContext.nowPlaying. Renders nothing when no book is in
            progress (so unauthenticated / first-time users see the original
            hero unchanged). */}
        <HomeContinueListeningHero />

        {/* Central Glyph */}
        <div className="mb-6 md:mb-8 flex justify-center">
          <div
            ref={heroGlyphRef}
            className="relative cursor-pointer"
            style={{
              width: '72px',
              height: '72px',
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onMouseEnter={() => setIsGlyphHovered(true)}
            onMouseLeave={() => setIsGlyphHovered(false)}
          >
            {/* Magical Sigil SVG */}
            <svg
              width="72"
              height="72"
              viewBox="0 0 96 96"
              className="md:w-24 md:h-24"
              style={{
                filter: `drop-shadow(0 4px 12px rgba(215, 179, 119, 0.4))`,
                animation: isGlyphHovered ? 'spin 1s ease-in-out' : 'spin 15s linear infinite'
              }}
            >
              <defs>
                <linearGradient id="glyphGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#D7B377" />
                  <stop offset="100%" stopColor="#E2D7C4" />
                </linearGradient>
              </defs>
              
              {/* Compass Rose Base */}
              <circle cx="48" cy="48" r="40" fill="none" stroke="url(#glyphGradient)" strokeWidth="2" opacity="0.6"/>
              <circle cx="48" cy="48" r="30" fill="none" stroke="url(#glyphGradient)" strokeWidth="1" opacity="0.4"/>
              
              {/* Main Directional Points */}
              <path d="M48 8 L52 24 L48 20 L44 24 Z" fill="url(#glyphGradient)"/>
              <path d="M88 48 L72 44 L76 48 L72 52 Z" fill="url(#glyphGradient)"/>
              <path d="M48 88 L44 72 L48 76 L52 72 Z" fill="url(#glyphGradient)"/>
              <path d="M8 48 L24 52 L20 48 L24 44 Z" fill="url(#glyphGradient)"/>
              
              {/* Sound Wave Elements */}
              <path d="M32 48 Q38 42, 44 48 Q38 54, 32 48" fill="none" stroke="url(#glyphGradient)" strokeWidth="2"/>
              <path d="M64 48 Q58 42, 52 48 Q58 54, 64 48" fill="none" stroke="url(#glyphGradient)" strokeWidth="2"/>
              
              {/* Central Book/Seal */}
              <circle cx="48" cy="48" r="8" fill="url(#glyphGradient)" opacity="0.8"/>
              <rect x="44" y="44" width="8" height="8" fill="#1A202C" opacity="0.6"/>
              
              {/* Decorative Stars */}
              <path d="M24 24 L26 28 L30 26 L26 30 L24 34 L22 30 L18 26 L22 28 Z" fill="url(#glyphGradient)" opacity="0.7"/>
              <path d="M72 24 L74 28 L78 26 L74 30 L72 34 L70 30 L66 26 L70 28 Z" fill="url(#glyphGradient)" opacity="0.7"/>
              <path d="M24 72 L26 76 L30 74 L26 78 L24 82 L22 78 L18 74 L22 76 Z" fill="url(#glyphGradient)" opacity="0.7"/>
              <path d="M72 72 L74 76 L78 74 L74 78 L72 82 L70 78 L66 74 L70 76 Z" fill="url(#glyphGradient)" opacity="0.7"/>
            </svg>
            
            {/* Glyph Hover Effect */}
            {isGlyphHovered && (
              <div className="absolute inset-0 rounded-full" style={{
                background: 'radial-gradient(circle, rgba(215, 179, 119, 0.2) 0%, transparent 70%)',
                animation: 'pulse 0.6s ease-out'
              }} />
            )}
          </div>
        </div>

        {/* Headlines */}
        <div className="max-w-4xl mx-auto mb-8 md:mb-12">
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6"
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              lineHeight: '1.1',
              textShadow: '0 4px 8px rgba(0,0,0,0.3)',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F7F3E9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            Distinct Characters Deserve Distinctive Voices
          </h1>
          <p 
            className="text-lg sm:text-xl md:text-2xl mb-4 md:mb-6 max-w-3xl mx-auto px-2"
            style={{
              fontFamily: 'Inter, sans-serif',
              color: '#E2D7C4',
              fontWeight: '300',
              lineHeight: '1.6'
            }}
          >
            Our AI audiobook narration transforms any book into a multi-voice audiobook instantly. Distinct narrator voices assigned to each character, so dialogue sounds like a full cast performance.
          </p>
          <p 
            className="text-base md:text-lg opacity-80 max-w-2xl mx-auto px-2"
            style={{
              fontFamily: 'Inter, sans-serif',
              color: '#D7B377',
              fontWeight: '400'
            }}
          >
            121 classic titles ready to listen. 65+ professional AI voices. Try it free.
          </p>
        </div>

        {/* Primary CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-3">
          <EnhancedButtonLink 
            to="try-free"
            data-testid="landing-try-free-button"
            className="group relative px-6 md:px-10 py-3 md:py-4 rounded-xl text-base md:text-lg font-semibold transition-all duration-500 hover:scale-105 hover:shadow-xl inline-block"
            style={{
              background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
              color: '#1A202C',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: '700',
              border: 'none',
              boxShadow: '0 8px 24px rgba(215, 179, 119, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <span className="relative z-10">Try Free for 30 Days</span>
            
            {/* Magical underline effect */}
            <div 
              className="absolute bottom-1 left-1/2 transform -translate-x-1/2 h-0.5 bg-white transition-all duration-500 group-hover:w-20"
              style={{ width: '0' }}
            />
            
            {/* Ripple effect on click */}
            <div className="absolute inset-0 bg-white opacity-0 group-active:opacity-20 transition-opacity duration-150" />
          </EnhancedButtonLink>
          <EnhancedButtonLink 
            to="pricing"
            data-testid="landing-view-plans-button"
            className="px-6 md:px-10 py-3 md:py-4 rounded-xl text-base md:text-lg font-semibold transition-all duration-500 hover:scale-105 inline-block"
            style={{
              background: 'transparent',
              color: '#D7B377',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: '600',
              border: '1px solid rgba(215, 179, 119, 0.5)',
            }}
          >
            View Plans
          </EnhancedButtonLink>
        </div>
        <p className="text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif', color: '#CBD5E0', opacity: 0.8 }}>
          No promo code needed · $0 due today · cancel anytime
        </p>
        <EnhancedButtonLink
          to="how-it-works"
          data-testid="landing-how-it-works-link"
          className="inline-flex items-center gap-2 text-sm transition-all duration-300 hover:opacity-100"
          style={{
            fontFamily: 'Inter, sans-serif',
            color: '#1E2A3A',
            fontWeight: '600',
            textDecoration: 'underline',
            textUnderlineOffset: '4px',
          }}
        >
          ▶ See how it works — 90 seconds
        </EnhancedButtonLink>
      </main>

      {/* Voice of the Week — rotating weekly voice spotlight */}
      <VoiceOfTheWeek />

      {/* How It Works - SEO content section */}
      <section className="relative z-10 py-16 md:py-24" style={{ background: 'rgba(26,32,44,0.95)' }}>
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{
            fontFamily: 'DM Serif Display, Georgia, serif',
            color: '#D7B377',
          }}>
            How AI Audiobook Narration Works
          </h2>

          <div className="how-it-works-grid" style={{ display: 'flex', alignItems: 'flex-start', gap: '0', position: 'relative', marginBottom: '48px', padding: '20px 0' }}>
            {/* Connecting line */}
            <div className="how-it-works-line" style={{
              position: 'absolute', top: '44px', left: '60px', right: '60px', height: '2px',
              background: 'linear-gradient(90deg, #D7B37700, #D7B377, #D7B377, #D7B37700)',
            }} />

            {[
              { step: '1', title: 'Choose a Book', desc: 'Browse 121 classic titles — from Pride and Prejudice to Peter Rabbit — or upload your own manuscript.', offset: '0px' },
              { step: '2', title: 'AI Analyzes Characters', desc: 'Smart character analysis detects every character, identifies dialogue, and determines gender automatically.', offset: '30px' },
              { step: '3', title: 'Multi-Voice Narration', desc: 'Press play and hear each character speak in their own voice. The narrator handles exposition while characters sound distinct.', offset: '0px' },
              { step: '4', title: 'Customize & Clone', desc: 'Swap voices from 65+ AI voices, or clone your own voice for personal narration.', offset: '30px' },
            ].map((item, i) => (
              <div key={item.step} className="how-it-works-step" style={{
                flex: '1 1 0', minWidth: '220px', textAlign: 'center', padding: '0 16px',
                position: 'relative', marginTop: item.offset,
                transition: 'transform 0.3s ease',
              }}>
                {/* Step number node */}
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%', margin: '0 auto 20px',
                  background: 'linear-gradient(135deg, #D7B377, #DAA520)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#1A202C', fontWeight: 800, fontSize: '20px', fontFamily: 'DM Mono, monospace',
                  boxShadow: '0 0 20px rgba(215,179,119,0.3)',
                  position: 'relative', zIndex: 2,
                }}>
                  {item.step}
                </div>

                {/* Glass card */}
                <div style={{
                  background: 'rgba(255,255,255,0.04)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  borderRadius: '16px',
                  padding: '24px 20px',
                  border: '1px solid rgba(215,179,119,0.15)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  minHeight: '160px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                }}>
                  <h3 style={{ fontFamily: 'DM Serif Display, Georgia, serif', fontSize: '18px', color: '#fff', marginBottom: '10px' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#CBD5E0', lineHeight: 1.7, margin: 0 }}>
                    {item.desc}
                  </p>
                </div>

                {/* Arrow connector (between cards, not after last) */}
                {i < 3 && (
                  <div className="how-it-works-arrow" style={{
                    position: 'absolute', top: '44px', right: '-14px', zIndex: 3,
                    color: '#D7B377', fontSize: '18px', fontWeight: 700,
                  }}>
                    &#8250;
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Live Voice Demo Section */}
      <div className="relative z-10 py-20" style={{
        background: 'linear-gradient(180deg, rgba(26, 32, 44, 0) 0%, rgba(44, 58, 76, 0.8) 50%, rgba(26, 32, 44, 0) 100%)'
      }}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <p 
              className="text-2xl md:text-3xl mb-8"
              style={{
                fontFamily: 'DM Serif Display, Georgia, serif',
                color: '#F7F3E9',
                fontStyle: 'italic',
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                letterSpacing: '0.5px',
              }}
            >
              Experience the same passage in different voices...
            </p>
          </div>

          {/* Multi-Voice Demo Cards */}
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {Object.entries(DEMO_VOICES).map(([key, voice], index) => (
                <div
                  key={key}
                  data-testid={`multicast-voice-card-${voice.id}`}
                  className="group relative rounded-xl p-6 transition-all duration-500 hover:scale-105 cursor-pointer"
                  style={{
                    background: currentVoice === voice.id 
                      ? 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)'
                      : 'rgba(44, 58, 76, 0.6)',
                    border: individualVoicePlaying === voice.id 
                      ? '2px solid #D7B377' 
                      : '1px solid rgba(215, 179, 119, 0.3)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: currentVoice === voice.id 
                      ? '0 8px 24px rgba(215, 179, 119, 0.4)'
                      : '0 4px 12px rgba(0, 0, 0, 0.2)',
                    // Explicit text/icon colour so `bg-current` waveform bars and
                    // `fill="currentColor"` play icons inherit WCAG-AA contrast on
                    // both selected (gold) and idle (slate) backgrounds.
                    color: currentVoice === voice.id ? '#0F172A' : '#F8FAFC',
                    minHeight: '260px', // Fixed minimum height
                    width: '100%', // Fixed width
                    maxWidth: '280px', // Fixed maximum width
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onClick={() => switchVoice(voice.id, true)} // true enables individual voice demo
                >
                  {/* Category Badge */}
                  <div 
                    className="text-xs font-semibold px-3 py-1 rounded-full mb-3 inline-block"
                    style={{
                      background: currentVoice === voice.id ? 'rgba(26, 32, 44, 0.2)' : 'rgba(215, 179, 119, 0.2)',
                      color: currentVoice === voice.id ? '#1A202C' : '#D7B377',
                      fontFamily: 'DM Sans, sans-serif'
                    }}
                  >
                    {voice.category}
                  </div>
                  
                  <h3 
                    className="text-lg font-bold mb-2"
                    style={{
                      fontFamily: 'DM Serif Display, Georgia, serif',
                      color: currentVoice === voice.id ? '#0F172A' : '#FFFFFF'
                    }}
                  >
                    {voice.name}
                  </h3>
                  
                  <p 
                    className="text-sm opacity-80"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      color: currentVoice === voice.id ? '#1A202C' : '#E2D7C4'
                    }}
                  >
                    {voice.description}
                  </p>
                  
                  {/* Play/Pause Indicator for Individual Voice Cards */}
                  <div className="mt-3 flex items-center justify-between">
                    <span 
                      className="text-xs"
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        color: individualVoicePlaying === voice.id ? '#D7B377' : '#94A3B8',
                        fontWeight: '500'
                      }}
                    >
                      {individualVoiceLoading && currentVoice === voice.id 
                        ? 'Loading...' 
                        : individualVoicePlaying === voice.id 
                        ? `Playing ${individualVoiceTimepoint > 0 ? `(${Math.floor(individualVoiceTimepoint)}s)` : ''}` 
                        : (lastSelectedVoice === voice.id && individualVoiceTimepoint > 0)
                        ? `Resume (${Math.floor(individualVoiceTimepoint)}s)`
                        : 'Click to Play'}
                    </span>
                    
                    {/* Enhanced Play/Pause Icon with timepoint indication */}
                    <div style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.7,
                      position: 'relative'
                    }}>
                      {individualVoiceLoading && currentVoice === voice.id ? (
                        <div style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid currentColor',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                      ) : individualVoicePlaying === voice.id ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                          <rect x="2" y="1" width="2" height="10" rx="1"/>
                          <rect x="8" y="1" width="2" height="10" rx="1"/>
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                          <path d="M2 1v10l8-5z"/>
                        </svg>
                      )}
                      
                      {/* Small dot indicator for timepoint persistence */}
                      {(lastSelectedVoice === voice.id && individualVoiceTimepoint > 0 && !individualVoicePlaying) && (
                        <div style={{
                          position: 'absolute',
                          bottom: '-2px',
                          right: '-2px',
                          width: '4px',
                          height: '4px',
                          borderRadius: '50%',
                          backgroundColor: '#D7B377'
                        }} />
                      )}
                    </div>
                  </div>
                  
                  {/* Audio Wave Visualization */}
                  <div className="mt-4 flex items-center justify-center space-x-1" style={{
                    height: '40px', // Fixed height for waveform container
                    width: '100%'
                  }}>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div
                        key={i}
                        className="w-1 bg-current rounded-full transition-all duration-300"
                        style={{
                          height: `${Math.random() * 20 + 8}px`,
                          // Higher idle opacity so slate bars stay legible on the
                          // gold-selected card and white bars stay visible on the
                          // dark idle card. WCAG AA contrast required.
                          opacity: (individualVoicePlaying === voice.id || currentVoice === voice.id) ? 1 : 0.55,
                          ...(individualVoiceLoading || (individualVoicePlaying === voice.id) ? {
                            animationName: 'waveform',
                            animationDuration: '1s',
                            animationTimingFunction: 'ease-in-out',
                            animationIterationCount: 'infinite',
                            animationDelay: `${i * 0.1}s`
                          } : {
                            animationName: 'none'
                          })
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Enhanced Demo Text with Character Highlighting */}
            <div 
              className="rounded-xl p-8"
              style={{
                background: 'rgba(26, 32, 44, 0.8)',
                border: '1px solid rgba(215, 179, 119, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <div className="mb-6">
                <h3 
                  className="text-xl font-bold mb-4 text-center"
                  style={{
                    fontFamily: 'DM Serif Display, Georgia, serif',
                    color: '#D7B377'
                  }}
                >
                  A Tale of Two Cities
                </h3>
                <div 
                  className="text-lg leading-relaxed text-center italic"
                  style={{
                    fontFamily: 'DM Serif Display, Georgia, serif',
                    color: '#E2D7C4',
                    lineHeight: '1.8'
                  }}
                >
                  <span style={{ color: '#F7F3E9' }}>
                    It was the best of times, it was the worst of times, it was the age of wisdom, 
                    it was the age of foolishness...
                  </span>
                </div>
              </div>
              
              {/* Play All Demo Button */}
              <div className="text-center">
                <button 
                  onClick={() => togglePlayback(true)} // true for multi-voice demo
                  className="group px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
                  style={{
                    background: isPlaying 
                      ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                      : 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
                    color: isPlaying ? '#FFFFFF' : '#1A202C',
                    fontFamily: 'DM Sans, sans-serif',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(215, 179, 119, 0.3)'
                  }}
                  disabled={loading}
                >
                  <span className="flex items-center space-x-3">
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px'
                    }}>
                      {loading ? (
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid currentColor',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                      ) : isPlaying ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <rect x="3" y="2" width="3" height="12" rx="1"/>
                          <rect x="10" y="2" width="3" height="12" rx="1"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M3 2v12l10-6z"/>
                        </svg>
                      )}
                    </span>
                    <span>
                      {loading ? 'Loading...' : isPlaying ? 'Stop Demo' : 'Play Multi-Voice Demo'}
                    </span>
                  </span>
                </button>
              </div>
              
              {/* Current Playing Voice Indicator */}
              {individualVoicePlaying && (
                <div 
                  className="mt-6 text-center"
                  style={{
                    padding: '12px 24px',
                    background: 'rgba(215, 179, 119, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(215, 179, 119, 0.3)'
                  }}
                >
                  <span 
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '14px',
                      color: '#D7B377',
                      fontWeight: '500'
                    }}
                  >
                    Now Playing: {individualVoiceName}
                  </span>
                  {individualVoiceTimepoint > 0 && (
                    <span 
                      style={{
                        marginLeft: '8px',
                        color: '#E2D7C4',
                        opacity: 0.8
                      }}
                    >
                      ({Math.floor(individualVoiceTimepoint)}s)
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Why Readers Choose VoxSmyth */}
      <section className="relative z-10 py-16 md:py-20" style={{ background: "rgba(26,32,44,0.95)" }}>
        <div className="container mx-auto px-6">
          {/* SEO content block - features & social proof */}
          <div style={{ maxWidth: '720px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="text-xl md:text-2xl font-bold mb-4" style={{
              fontFamily: 'DM Serif Display, Georgia, serif', color: '#E8DCC0',
            }}>
              Why Readers Choose VoxSmyth for AI Audiobook Narration
            </h2>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#A0AEC0', lineHeight: 1.8, marginBottom: '16px' }}>
              Traditional audiobooks use a single narrator for every character. VoxSmyth changes that with AI-powered multi-voice narration — each character in your book gets their own distinct voice, turning every story into a full-cast audio experience.
            </p>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#A0AEC0', lineHeight: 1.8, marginBottom: '16px' }}>
              Powered by ElevenLabs' studio-quality text-to-speech, our platform offers over 65 professional AI voices spanning male, female, British, American, and Australian accents. Choose from classic narrators or create your own voice clone — perfect for parents who want to read bedtime stories in their own voice, even when they're away.
            </p>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#A0AEC0', lineHeight: 1.8, marginBottom: '24px' }}>
              Our library includes 121 public domain classics — from children's favorites like The Tale of Peter Rabbit, Grimm's Fairy Tales, and Aesop's Fables, to literary masterpieces like Jane Eyre, Wuthering Heights, and War and Peace. New titles are added regularly. Authors and creators can upload their own manuscripts for instant AI narration with multi-voice character casting.
            </p>
          </div>

          {/* Authors & Voice Artists - Expandable */}
          <details style={{ maxWidth: '720px', margin: '32px auto 0', cursor: 'pointer' }}>
            <summary style={{
              fontFamily: 'DM Serif Display, Georgia, serif', fontSize: '20px', color: '#D7B377',
              listStyle: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
              padding: '16px', borderRadius: '12px', border: '1px solid #D7B37730',
              background: 'rgba(215,179,119,0.05)', transition: 'all 0.3s ease',
            }}>
              <span>Authors & Voice Artists</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ transition: 'transform 0.3s' }}>
                <path d="M6 9l6 6 6-6" stroke="#D7B377" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </summary>
            <div style={{
              padding: '28px 24px', marginTop: '12px', borderRadius: '12px',
              background: 'rgba(255,255,255,0.03)', border: '1px solid #D7B37715',
            }}>
              <h3 style={{ fontFamily: 'DM Serif Display, Georgia, serif', fontSize: '22px', color: '#E8DCC0', marginBottom: '12px', textAlign: 'center' }}>
                Enter the Inner Circle
              </h3>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#A0AEC0', lineHeight: 1.8, marginBottom: '16px', textAlign: 'center' }}>
                Are you an author looking to turn your manuscript into a professional audiobook? Or a voice artist who wants to reach new audiences? VoxSmyth's Creator tier gives authors full manuscript upload and multi-voice narration tools, while our Voice Actor program lets you clone your voice, set your rate, and earn royalties when listeners choose your voice.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <EnhancedButtonLink
                  to="creator-intake"
                  style={{
                    padding: '10px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                    background: 'transparent', border: '1px solid #D7B377', color: '#D7B377',
                    fontFamily: 'DM Sans, sans-serif', textDecoration: 'none',
                  }}
                >
                  Apply as Creator
                </EnhancedButtonLink>
                <EnhancedButtonLink
                  to="voice-actor-portal"
                  style={{
                    padding: '10px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
                    background: 'linear-gradient(135deg, #D7B377, #DAA520)', border: 'none', color: '#1A202C',
                    fontFamily: 'DM Sans, sans-serif', textDecoration: 'none',
                  }}
                >
                  Join as Voice Artist
                </EnhancedButtonLink>
              </div>
            </div>
          </details>
        </div>
      </section>

      {/* Begin Your Story CTA */}
      <div className="relative z-10 py-16 text-center" style={{ background: 'rgba(26,32,44,0.9)' }}>
        <p style={{
          fontFamily: 'DM Serif Display, Georgia, serif', fontStyle: 'italic',
          fontSize: '20px', color: '#D7B377', marginBottom: '24px', opacity: 0.85,
        }}>
          "Where every voice tells a story, and every story finds its voice"
        </p>
        <EnhancedButtonLink
          to="try-free"
          data-testid="landing-bottom-try-free-button"
          className="group px-10 py-4 rounded-xl text-lg font-bold transition-all duration-500 hover:scale-105 inline-block"
          style={{
            background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
            color: '#1A202C',
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: '800',
            border: 'none',
            boxShadow: '0 12px 32px rgba(215, 179, 119, 0.35)',
            textDecoration: 'none',
          }}
        >
          Begin Your Story — Free for 30 Days
        </EnhancedButtonLink>
      </div>

      {/* Global Animation Styles - Fixed JSX attribute issue */}
      <style>{`

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }

        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% { transform: translateY(0); }
          40%, 43% { transform: translateY(-8px); }
          70% { transform: translateY(-4px); }
          90% { transform: translateY(-2px); }
        }

        @keyframes waveform {
          0%, 100% { height: 8px; opacity: 0.3; }
          50% { height: 24px; opacity: 1; }
        }

        @keyframes wave-pulse {
          0%, 100% { 
            opacity: 0.4; 
            transform: translateY(0); 
          }
          50% { 
            opacity: 1; 
            transform: translateY(-2px); 
          }
        }

        @keyframes sparkle-float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg);
            opacity: 0.3;
          }
          50% { 
            transform: translateY(-10px) rotate(180deg);
            opacity: 1;
          }
        }

        .min-h-screen {
          min-height: 100vh;
        }

        .overflow-x-hidden {
          overflow-x: hidden;
        }

        .container {
          max-width: 1200px;
        }

        .text-center {
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default EnhancedLanding;